<script lang="ts">
  import KeyboardHints from '../KeyboardHints.svelte';
  import { hasCommandModifier, isEditableTarget } from '../keyboard';
  import type { LabStep } from '../../lib/router';
  import {
    DICT_PUZZLES,
    dictModel,
    lookup,
    parseKey,
    parseValue,
    preparedDictPuzzles,
    shuffledKeys,
    vizDict,
    vizSet,
    swiftArray,
    type DictOp,
  } from '../../lib/labs';
  import DictBoard from './DictBoard.svelte';
  import LabLog from './LabLog.svelte';
  import PredictPanel from './PredictPanel.svelte';
  import ReplayPanel from './ReplayPanel.svelte';
  import LockerIntro from './LockerIntro.svelte';
  import CopyShelves from './CopyShelves.svelte';
  import { finishIntro, intro, prefersReducedMotion, sessions, slowMs } from './lab-session.svelte';

  /** ラボ 3（labs.md、components.md §3.2 DictLab）。 */
  interface Props {
    step: LabStep;
    ongo: (step: LabStep) => void;
  }

  let { step, ongo }: Props = $props();

  const session = sessions.dict;
  const puzzles = preparedDictPuzzles();
  const reduced = prefersReducedMotion();

  let keyText = $state('x');
  let valueText = $state('5');
  let keyInput: HTMLInputElement | undefined = $state();
  let valueInput: HTMLInputElement | undefined = $state();
  let status = $state('');
  let activeKey: string | null = $state(null);
  let missingKey: string | null = $state(null);
  let activeMember: number | null = $state(null);
  let counting = $state(false);

  const st = $derived(session.state);
  /** 操作のたびに並べ替える（順序が決まっていないことを見た目で伝える）。 */
  const lockers = $derived.by(() => {
    void session.tick;
    return shuffledKeys(st, Math.random).map((k): [string, number] => [k, lookup(st, k) ?? 0]);
  });
  const lastTag = $derived(reduced && session.last?.entry ? session.last.entry.tag : null);

  function say(text: string) {
    status = text;
  }

  function apply(op: DictOp) {
    const r = session.apply(op);
    say(r.message);
    activeKey = 'key' in op && lookup(r.state, op.key) !== undefined ? op.key : null;
    missingKey = op.type === 'get' && lookup(r.state, op.key) === undefined ? op.key : null;
    activeMember = op.type === 'insert' || op.type === 'contains' ? op.value : null;
  }

  function needKey(): string | null {
    const k = parseKey(keyText);
    if (k === null) {
      say('キーは英小文字で始まる 8 文字までの英小文字・数字です（例: apple）。');
      keyInput?.focus();
    }
    return k;
  }

  function needValue(): number | null {
    const v = parseValue(valueText);
    if (v === null) {
      say('値に -999〜999 の整数を入れてください。');
      valueInput?.focus();
    }
    return v;
  }

  function put() {
    const key = needKey();
    if (key === null) return;
    const value = needValue();
    if (value === null) return;
    apply({ type: 'set', key, value });
  }
  function get() {
    const key = needKey();
    if (key !== null) apply({ type: 'get', key });
  }
  function inc() {
    const key = needKey();
    if (key !== null) apply({ type: 'increment', key });
  }
  function remove() {
    const key = needKey();
    if (key !== null) apply({ type: 'remove', key });
  }
  function insert() {
    const value = needValue();
    if (value !== null) apply({ type: 'insert', value });
  }
  function contains() {
    const value = needValue();
    if (value !== null) apply({ type: 'contains', value });
  }

  /** labs.md ラボ 3 手順 2: banana を 1 文字ずつ流し、「1 増やす」を自動で押す。 */
  async function countWord(word: string) {
    if (counting) return;
    counting = true;
    const wait = Math.max(slowMs(), 0);
    for (const ch of word) {
      keyText = ch;
      apply({ type: 'increment', key: ch });
      if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    }
    counting = false;
    say(`${word} を数えました。各キーの値が、その文字の回数です。`);
  }

  function undo() {
    if (session.undo()) {
      activeKey = null;
      missingKey = null;
      say('1 手戻しました。');
    }
  }

  function reset() {
    session.reset();
    activeKey = null;
    missingKey = null;
    say('最初からにしました。');
  }

  function send() {
    session.sendToEditor();
    ongo(3);
  }

  function loadExample() {
    session.reset();
    for (const op of DICT_PUZZLES[0]?.ops ?? []) session.apply(op);
  }

  function onkeydown(e: KeyboardEvent) {
    if (step !== 1 || !intro.dict || e.defaultPrevented) return;
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'z') {
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      undo();
      return;
    }
    if (hasCommandModifier(e)) return;
    if (isEditableTarget(e.target)) {
      if ((e.target === valueInput || e.target === keyInput) && e.key === 'Enter') {
        e.preventDefault();
        put();
      } else if (e.key === 'Escape' && e.target instanceof HTMLElement) {
        e.target.blur();
      }
      return;
    }
    const actions: Record<string, () => void> = {
      s: put,
      g: get,
      a: inc,
      x: remove,
      i: insert,
      f: contains,
      k: () => {
        keyInput?.focus();
        keyInput?.select();
      },
      v: () => {
        valueInput?.focus();
        valueInput?.select();
      },
    };
    const act = actions[e.key.toLowerCase()];
    if (act) {
      e.preventDefault();
      act();
    }
  }

  const footer = $derived(
    `print(dict.keys.sorted())  // ${swiftArray(vizDict(st.dict).map(([k]) => k))}`,
  );
</script>

<svelte:window {onkeydown} />

{#if !intro.dict}
  <LockerIntro ondone={() => finishIntro('dict')} />
{:else if step === 1}
  <div class="lab" data-testid="lab-dict">
    <section class="panel board" aria-labelledby="board-title" data-testid="lab-board-slot">
      <h2 id="board-title">盤面</h2>
      <DictBoard
        {lockers}
        roster={st.seen}
        {activeKey}
        {missingKey}
        {activeMember}
        changeTag={lastTag}
      />
      <p class="text-soft small">
        エントリの並び順は操作のたびに変わります。辞書の中身に順序は決まっていません。
      </p>
      <div class="controls">
        <div class="value field key">
          <label for="dict-key">キー <kbd>K</kbd></label>
          <input
            id="dict-key"
            type="text"
            autocomplete="off"
            spellcheck="false"
            bind:this={keyInput}
            bind:value={keyText}
          />
        </div>
        <div class="value field">
          <label for="dict-val">値 <kbd>V</kbd></label>
          <input
            id="dict-val"
            type="text"
            inputmode="numeric"
            bind:this={valueInput}
            bind:value={valueText}
          />
        </div>
      </div>
      <div class="controls" role="group" aria-label="辞書（dict）の操作">
        <button type="button" class="btn btn-primary" onclick={put} data-testid="dict-set"
          >入れる <kbd>S</kbd></button
        >
        <button type="button" class="btn" onclick={get} data-testid="dict-get"
          >開ける <kbd>G</kbd></button
        >
        <button type="button" class="btn" onclick={inc}>1 増やす <kbd>A</kbd></button>
        <button type="button" class="btn" onclick={remove}>消す <kbd>X</kbd></button>
      </div>
      <div class="controls" role="group" aria-label="Set（seen）の操作">
        <button type="button" class="btn" onclick={insert} data-testid="set-insert"
          >Set に入れる <kbd>I</kbd></button
        >
        <button type="button" class="btn" onclick={contains}>Set にあるか <kbd>F</kbd></button>
      </div>
      <div class="controls">
        <button
          type="button"
          class="btn btn-quiet mono"
          onclick={() => countWord('banana')}
          disabled={counting}>banana を数える</button
        >
        <button type="button" class="btn btn-quiet" onclick={undo}>1 手戻す <kbd>⌘Z</kbd></button>
        <button type="button" class="btn btn-quiet" onclick={reset}>最初から</button>
      </div>
      <p class="status-line" role="status" aria-live="polite">{status}</p>
      <dl class="counters" aria-live="polite">
        <div>
          <dt>辞書のエントリ数</dt>
          <dd class="num">{st.dict.length}</dd>
        </div>
        <div>
          <dt>Set の要素数</dt>
          <dd class="num">{st.seen.length}</dd>
        </div>
        <div>
          <dt>操作</dt>
          <dd class="num">{session.run.steps.length}</dd>
        </div>
        <div>
          <dt>今の操作で触ったエントリ</dt>
          <dd class="num">{session.last?.entry && session.last.entry.tag !== 'Set' ? 1 : 0}</dd>
        </div>
      </dl>
    </section>

    <section class="panel log" aria-labelledby="log-title" data-testid="lab-log-slot">
      <h2 id="log-title">記録（Swift）</h2>
      <LabLog
        declarations={dictModel.declarations(session.run.initial)}
        entries={session.entries}
        code={session.code}
        {footer}
        onsend={send}
      />
      <p class="text-soft small">
        無いキーを引くと <code>nil</code> です。だから <code>dict["x"]</code> は Optional
        で、数えるときは <code>default: 0</code> を使います。<code>Set</code>（seen）は
        <code>[{vizSet(st.seen).join(', ')}]</code> のように、同じ値を 2 回入れても 1 つです。
      </p>
    </section>
  </div>
  <CopyShelves />
  <KeyboardHints
    hints={[
      { keys: ['S'], label: '入れる' },
      { keys: ['G'], label: '開ける' },
      { keys: ['A'], label: '1 増やす' },
      { keys: ['X'], label: '消す' },
      { keys: ['I'], label: 'Set に入れる' },
      { keys: ['F'], label: 'Set にあるか' },
      { keys: ['K', 'V'], label: 'キー・値の欄へ' },
      { keys: ['⌘Z'], label: '1 手戻す' },
    ]}
  />
{:else if step === 2}
  <PredictPanel model={dictModel} {puzzles} labels={['dict', 'seen']} />
{:else}
  <ReplayPanel labId="dict" {session} labels={['dict', 'seen']} onexample={loadExample} />
{/if}

<style>
  .key {
    width: 8rem;
  }
  .mono {
    font-family: var(--font-mono);
  }
</style>
